import ApiServer from '@/utils/api/ApiServer.js'
import { Tract, StandingTimber, CommittedTimber, TractFilter, TractAnalysis, TimberPortfolio } from '@/model/Tract.js'
import { azureMapsApiKey } from '@/utils/map/MapHelpers.js'
import { get } from 'axios'
import moment from 'moment'
import persist from './persist.js'
import { localeCompareAlphanumeric } from '@/utils/base'
import BaseCost from '@/model/BaseCost.js'
import { parcelCache, searchCoordinatesWithParcelService } from '@/utils/map/Parcel.js'

export default {
  async fetchTracts (context, filter = new TractFilter()) {
    try {
      context.commit('setLoading', true)
      const path = `tracts?${filter.getQueryString()}`
      const response = await ApiServer.get(path)
      let tracts = response.data.map(tractResponse => new Tract(tractResponse))
      if (filter.selectedForesters.length > 0) {
        const selectedIds = filter.selectedForesters.map(f => f?.applicationUserId)
        tracts = tracts.filter(tract => selectedIds.includes(tract.foresterUser?.applicationUserId))
      }
      if (filter.selectedTractTypes.length > 0) {
        const selectedIds = filter.selectedTractTypes.map(tt => tt?.tractTypeId)
        tracts = tracts.filter(tract => selectedIds.includes(tract.type?.tractTypeId))
      }
      context.commit('setTracts', tracts)
      return tracts
    } finally {
      context.commit('setLoading', false)
    }
  },
  async fetchTractsOnce (_, filter = new TractFilter()) {
    const path = `tracts?${filter.getQueryString()}`
    const { data } = await ApiServer.get(path)
    return data.map(t => new Tract(t))
  },
  async fetchTract (_, tractId) {
    const path = `tracts/${tractId}`
    const response = await ApiServer.get(path)
    if (response.data.users) response.data.users.sort((a, b) => localeCompareAlphanumeric(a.name, b.name))
    return response.data
  },
  async deleteTract (context, tractId) {
    const response = await ApiServer.delete(`tracts/${tractId}`)
    context.commit('removeTract', tractId)
    return response.data
  },
  async createTract (_, tract) {
    const response = await ApiServer.post('tracts', tract, {
      headers: {
        tractTypeId: tract.tractTypeId
      }
    })
    return response.data
  },
  async fetchTractGroups (context) {
    try {
      context.commit('setTractGroupsLoading', true)
      const response = await ApiServer.get('tractgroups')
      const tractGroups = response.data
      context.commit('setTractGroups', tractGroups)
      return tractGroups
    } finally {
      context.commit('setTractGroupsLoading', false)
    }
  },
  async createTractGroup (_, tractGroup) {
    const response = await ApiServer.post('tractgroups', tractGroup, {
      headers: {
        tractTypeId: tractGroup.tractTypeId
      }
    })
    return response.data
  },
  async updateTractGroup (context, tractGroup) {
    const path = `tractgroups/${tractGroup.tractGroupId}`
    const response = await ApiServer.put(path, tractGroup, {
      headers: {
        tractTypeId: tractGroup.tractTypeId
      }
    })
    context.commit('updateTractGroup', response.data)
    return response.data
  },
  async deleteTractGroup (context, tractGroupId) {
    const response = await ApiServer.delete(`tractgroups/${tractGroupId}`)
    context.commit('removeTractGroup', tractGroupId)
    return response.data
  },
  async fetchTractGroup (_, tractGroupId) {
    const response = await ApiServer.get(`tractgroups/${tractGroupId}`)
    return response.data
  },
  async fetchTractTimberVolume (context, payload) {
    try {
      context.commit('setLoading', true)
      const path = `tracts/${payload.tractId}/timbervolume?lowerBound=${payload.startDate}&upperBound=${payload.endDate}`
      const response = await ApiServer.get(path)
      context.commit('setTractTimberVolume', response.data)
      return response.data
    } finally {
      context.commit('setLoading', false)
    }
  },
  async getLoggersForTract (_, tractId) {
    const path = `tracts/${tractId}/loggers`
    const response = await ApiServer.get(path)
    return response.data
  },
  async getTractWithId (_, tractId) {
    const path = `tracts/${tractId}`
    const response = await ApiServer.get(path)
    return response.data
  },

  async updateTract (_, tract) {
    const path = `tracts/${tract.tractId}`
    const headers = {
      forceDownChanges: tract.shouldApplyDates ?? false
    }

    const response = await ApiServer.put(path, tract, { headers: headers })
    return response.data
  },

  async fetchTractPayables (_, tractId) {
    const path = `tracts/${tractId}/tractPayables`
    const response = await ApiServer.get(path)
    return response.data
  },

  async createTractPayment (_, requestObj) {
    const { tractId, tractPayment } = requestObj
    const path = `tracts/${tractId}/tractpayables`
    const response = await ApiServer.post(path, tractPayment)
    return response.data
  },

  async updateTractPayment (_, requestObj) {
    const { tractId, tractPayment } = requestObj
    tractPayment.tractId = tractId
    const path = `tracts/${tractId}/tractpayables/${tractPayment.tractPayableId}`
    const response = await ApiServer.put(path, tractPayment)
    return response.data
  },

  async deleteTractPayable (_, requestObj) {
    const { tractId, tractPayableId } = requestObj
    const path = `tracts/${tractId}/tractpayables/${tractPayableId}`
    const response = await ApiServer.delete(path)
    return response.data
  },

  async fetchTractMitigationItems (_, tractId) {
    const path = `tracts/${tractId}/mitigationitems`
    const response = await ApiServer.get(path)
    return (response.data ?? []).map(item => {
      const habitats = [...(item.habitats ?? [])].sort((a, b) => a.name.localeCompare(b.name))
      return { ...item, habitats, mitigationHabitatIds: habitats.map(h => h.mitigationHabitatId) }
    })
  },

  async createTractMitigationItem (_, requestObj) {
    const { tractId, mitigationItem } = requestObj
    mitigationItem.tractId = tractId
    const path = `tracts/${tractId}/mitigationitems`
    const response = await ApiServer.post(path, mitigationItem)
    return response.data
  },

  async updateTractMitigationItem (_, requestObj) {
    const { tractId, mitigationItem } = requestObj
    mitigationItem.tractId = tractId
    const path = `tracts/${tractId}/mitigationitems/${mitigationItem.tractMitigationItemId}`
    const response = await ApiServer.put(path, mitigationItem)
    return response.data
  },

  async deleteTractMitigationItem (_, requestObj) {
    const { tractId, tractMitigationItemId } = requestObj
    const path = `tracts/${tractId}/mitigationitems/${tractMitigationItemId}`
    const response = await ApiServer.delete(path)
    return response.data
  },

  async updateAllTractMitigationItems (_, requestObj) {
    const { tractId, mitigationItems } = requestObj
    const path = `tracts/${tractId}/mitigationitems`
    const response = await ApiServer.put(path, mitigationItems)
    return response.data ?? []
  },

  async setTractDueDiligenceExemption (_, requestObj) {
    const { tractId, dueDiligenceExemptionId, modifiedAt } = requestObj
    const path = `tracts/${tractId}`
    const body = [
      {
        op: 'add',
        path: '/dueDiligenceExemptionId',
        value: dueDiligenceExemptionId ?? null
      }
    ]
    const headers = modifiedAt ? { modifiedAt } : {}
    const response = await ApiServer.patch(path, body, { headers })
    return response.data
  },

  async patchTractStatus (_, requestObj) {
    const { tractId, status, modifiedAt } = requestObj
    const path = `tracts/${tractId}`
    const body = [
      {
        op: 'add',
        path: '/status',
        value: status
      }
    ]
    const headers = modifiedAt ? { modifiedAt } : {}
    const response = await ApiServer.patch(path, body, { headers })
    return response.data
  },

  async submitDueDiligence (_, tractId) {
    const path = `tracts/${tractId}/duediligence`
    const response = await ApiServer.post(path)
    return response.data
  },

  async getTractProductCosts (_, params) {
    let path = 'tracts/tractCosts?'
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        path += `${key}=${value}&`
      }
    }
    const response = await ApiServer.get(path)
    return response.data
  },

  async getTractSpeciesCosts (_, { businessEntityId, ...params }) {
    const path = ApiServer.urlFor('tracts/tractCostsbyspecies', {
      version: 1,
      params: businessEntityId !== null ? { businessEntityId, ...params } : params
    })
    const response = await ApiServer.get(path)
    return response.data
  },

  async getDetailedTractProductCosts (_, { tractId, businessEntityId, params }) {
    let path = `tracts/${tractId}/tractCosts`
    if (businessEntityId !== null) {
      path = `${path}?businessEntityId=${businessEntityId}&`
    } else {
      path = `${path}?`
    }
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        path += `${key}=${value}&`
      }
    }
    const response = await ApiServer.get(path)
    return response.data
  },

  async getDetailedTractSpeciesCosts (_, { tractId, businessEntityId, params }) {
    const path = ApiServer.urlFor(`tracts/${tractId}/tractCostsbyspecies`, {
      version: 1,
      params: businessEntityId !== null ? { businessEntityId, ...params } : params
    })
    const response = await ApiServer.get(path)
    return response.data
  },

  async getTractHarvest (_, { tractId, asOfDate }) {
    const path = ApiServer.urlFor(`tracts/${tractId}/tractHarvest`, {
      params: {
        asAt: asOfDate ? moment.utc(asOfDate).add(1, 'days').format() : undefined
      }
    })
    const { data: tractHarvestData } = await ApiServer.get(path)
    return new TractAnalysis(tractHarvestData)
  },

  async getTractMetrics (_, { tractId, startDate, endDate }) {
    const path = ApiServer.urlFor(`tractMetrics/${tractId}`, {
      params: {
        sinceTime: startDate,
        untilTime: endDate,
        includeInternal: true,
        includeExternal: true,
        useLoadCreatedAt: true
      }
    })
    const { data: tractMetricsData } = await ApiServer.get(path)
    return tractMetricsData
  },

  async fetchTractTickets (context, { tractId, page = 1, pageSize = 100, sinceTime, untilTime }) {
    const url = ApiServer.urlFor(['tracts', tractId, 'tickets'], {
      params: { page, pageSize, sinceTime, untilTime },
      version: 2
    })
    const response = await ApiServer.get(url)
    return response.data
  },

  async fetchAllTractTickets (context, { tractId, sinceTime, untilTime }) {
    const pageSize = 500
    const first = await context.dispatch('fetchTractTickets', { tractId, page: 1, pageSize, sinceTime, untilTime })
    const all = [...first.items]
    for (let page = 2; page <= first.totalPages; page++) {
      const next = await context.dispatch('fetchTractTickets', { tractId, page, pageSize, sinceTime, untilTime })
      all.push(...next.items)
    }
    return all
  },

  async fetchStandingTimber (context, { asOfDate, businessEntityId, statuses, tractTypes, tractForesters, teams, myTracts }) {
    if (!(statuses?.length > 0) && !(tractTypes?.length > 0)) return []
    context.commit('setLoading', true)
    try {
      if (asOfDate) {
        asOfDate = moment.utc(asOfDate).add(1, 'days').format()
      }
      const statusSet = new Set(statuses)
      const url = ApiServer.urlFor('dashboard/standingTimber', {
        params: {
          asAt: asOfDate ?? undefined,
          businessEntityId: businessEntityId ?? undefined,
          includeExecuted: statusSet.has(2),
          includeActive: statusSet.has(3),
          includeClosed: statusSet.has(4),
          tractTypeIds: tractTypes.length > 0 ? tractTypes.map(tt => tt.tractTypeId).join(',') : undefined,
          tractForesterIds: tractForesters.length > 0 ? tractForesters.map(tf => tf.applicationUserId).join(',') : undefined,
          teamIds: teams.length > 0 ? teams.map(t => t.teamId).join(',') : undefined,
          limitToMyTracts: myTracts
        }
      })
      const { data: standingTimber } = await ApiServer.get(url)
      return standingTimber.map(t => new StandingTimber(t))
    } finally {
      context.commit('setLoading', false)
    }
  },

  async fetchCommittedTimber (context, { statuses = [], tractTypes = [], tractForesters = [], teams = [], myTracts = false } = {}) {
    if (!(statuses?.length > 0) && !(tractTypes?.length > 0)) return []
    context.commit('setLoading', true)
    try {
      const statusSet = new Set(statuses)
      const url = ApiServer.urlFor('dashboard/committedTimber', {
        params: {
          includeInitiated: statusSet.has(0),
          includeCruised: statusSet.has(1),
          includeActive: statusSet.has(3),
          tractTypeIds: tractTypes.length > 0 ? tractTypes.map(tt => tt.tractTypeId).join(',') : undefined,
          tractForesterIds: tractForesters.length > 0 ? tractForesters.map(tf => tf.applicationUserId).join(',') : undefined,
          teamIds: teams.length > 0 ? teams.map(t => t.teamId).join(',') : undefined,
          limitToMyTracts: myTracts
        }
      })
      const { data: committedTimber } = await ApiServer.get(url)
      return committedTimber.map(t => new CommittedTimber(t))
    } finally {
      context.commit('setLoading', false)
    }
  },

  async fetchTimberPortfolio (context, { statuses = [], tractTypes = [], tractForesters = [], teams = [], myTracts = false } = {}) {
    if (!(statuses?.length > 0) && !(tractTypes?.length > 0)) return []
    context.commit('setLoading', true)
    try {
      const statusSet = new Set(statuses)
      const url = ApiServer.urlFor('dashboard/timberPortfolio', {
        params: {
          includeExecuted: statusSet.has(2),
          includeActive: statusSet.has(3),
          tractTypeIds: tractTypes.length > 0 ? tractTypes.map(tt => tt.tractTypeId) : undefined,
          tractForesterIds: tractForesters.length > 0 ? tractForesters.map(tf => tf.applicationUserId) : undefined,
          teamIds: teams.length > 0 ? teams.map(t => t.teamId) : undefined,
          limitToMyTracts: myTracts
        }
      })
      const { data: timberPortfolio } = await ApiServer.get(url)
      return new TimberPortfolio(timberPortfolio)
    } finally {
      context.commit('setLoading', false)
    }
  },

  async getLocationWithCoordinate (_, coordinate) {
    try {
      const result = await searchCoordinatesWithAzure(coordinate)
      return result
    } catch (error) {
      console.log(error)
    }

    // If for some reason Azure Map Service is down, hopefully the government isn't.
    try {
      const result = await searchCoordinatesWithFCC(coordinate)
      return result
    } catch (error) {
      console.log(error)
    }

    throw new Error('Error getting results from API.')
  },

  async getLocationWithCompanyAddress ({ rootState }, _address = undefined) {
    const address = _address ?? rootState.user.companyInfo?.address

    if (!address) {
      throw new Error('Could not retrive address from company info.')
    }

    const result = await searchAddressWithAzure(address)
    return result
  },

  async getWeatherForTract (_, coordinatesDatesUnits) {
    try {
      const result = await searchWeatherWithAzure(coordinatesDatesUnits)
      return result
    } catch (error) {
      console.log(error)
      throw new Error('Error getting weather results from API.')
    }
  },

  async fetchTractLandowners (_, tractId) {
    const path = `tracts/${tractId}/tractlandowners`
    const response = await ApiServer.get(path)
    return response.data
  },

  async fetchContractsForTract (_, tractId) {
    const path = `v2/tracts/${tractId}/contracts`
    const response = await ApiServer.get(path)
    return response.data
  },

  async fetchRatesForTractContracts (_, { tractId, contractIds }) {
    let path = `v2/tracts/${tractId}/contracts/rates`
    if (contractIds && contractIds.length > 0) {
      const params = new URLSearchParams()
      contractIds.forEach(id => params.append('contractIds', id))
      path += `?${params.toString()}`
    }
    const response = await ApiServer.get(path)
    return response.data
  },

  async fetchActiveTractsForPeriod (_, { startDate, endDate }) {
    const url = ApiServer.urlFor('activeTractsInPeriod', {
      params: { sinceTime: startDate, untilTime: endDate }
    })
    const response = await ApiServer.get(url)
    return response.data
  },

  setStandingTimberFilter (context, filter) {
    context.commit('setStandingTimberFilter', filter)
  },

  readStandingTimberFilter (context) {
    const filter = persist.standingTimberFilter.read(context)
    if (filter !== undefined) context.commit('setStandingTimberFilter', filter)
  },

  setCommittedTimberFilter (context, filter) {
    context.commit('setCommittedTimberFilter', filter)
  },

  readCommittedTimberFilter (context) {
    const filter = persist.committedTimberFilter.read(context)
    if (filter !== undefined) context.commit('setCommittedTimberFilter', filter)
  },

  async fetchDepletion (_, { fiscalYearId, payPeriodId, filter }) {
    const path = payPeriodId === undefined
      ? `v1/fiscalyears/${fiscalYearId}/tractdepletion`
      : `v1/fiscalyears/${fiscalYearId}/payperiods/${payPeriodId}/tractdepletion`

    const response = await ApiServer.get(path)
    return response.data
  },

  async fetchObligation (_, { fiscalYearId, payPeriodId, filter }) {
    const path = payPeriodId === undefined
      ? `v1/fiscalyears/${fiscalYearId}/tractobligation`
      : `v1/fiscalyears/${fiscalYearId}/payperiods/${payPeriodId}/tractobligation`

    const response = await ApiServer.get(path)
    return response.data
  },

  setTimberPortfolioFilter (context, filter) {
    context.commit('setTimberPortfolioFilter', filter)
  },

  readTimberPortfolioFilter (context) {
    const filter = persist.timberPortfolioFilter.read(context)
    if (filter !== undefined) context.commit('setTimberPortfolioFilter', filter)
  },

  async getParcelForCoordinate (_, coord) {
    let result = parcelCache.getParcelForCoordinate(coord)
    if (result) return result

    result = await searchCoordinatesWithParcelService(coord)
    if (result) parcelCache.addParcel(result.parcels)

    return result
  },

  async fetchTractGroupAnalysis (_, tractGroupId) {
    const path = `tractgroups/${tractGroupId}/analysis`
    const response = await ApiServer.get(path)
    return response.data
  },

  async fetchNamedRates (_, tractGroupId) {
    const path = `tractgroups/${tractGroupId}/namedrates`
    const response = await ApiServer.get(path)
    return response.data.map(nr => ({
      ...nr,
      costCalculation: new BaseCost(nr.costCalculation)
    }))
  },

  async fetchNamedRate (_, { tractGroupId, namedRateId }) {
    const path = `tractgroups/${tractGroupId}/namedrates/${namedRateId}`
    const response = await ApiServer.get(path)
    return response.data
  },

  async createNamedRate (_, requestObj) {
    const path = `tractgroups/${requestObj.tractGroupId}/namedrates`
    const response = await ApiServer.post(path, requestObj)
    return response.data
  },

  async deleteNamedRate (_, { tractGroupId, namedRateId }) {
    const path = `tractgroups/${tractGroupId}/namedrates/${namedRateId}`
    const response = await ApiServer.delete(path)
    return response.data
  },

  async updateNamedRate (_, requestObj) {
    const path = `tractgroups/${requestObj.tractGroupId}/namedrates/${requestObj.namedRateId}`
    const response = await ApiServer.put(path, requestObj)
    return response.data
  },

  async fetchConversions (context) {
    const response = await ApiServer.get('conversions')
    const sortedData = response.data.sort((a, b) => a.name.localeCompare(b.name))
    context.commit('setConversions', sortedData)
    return sortedData
  },

  async deleteConversion (context, conversionId) {
    const response = await ApiServer.delete(`conversions/${conversionId}`)
    context.commit('removeConversion', conversionId)
    return response.data
  },

  async createConversion (context, conversion) {
    const response = await ApiServer.post('conversions', conversion)
    context.commit('addConversion', response.data)
    return response.data
  },

  async updateConversion (context, conversion) {
    let basePath = `conversions/${conversion.conversionId}`
    if (conversion.businessEntityId) basePath += `?businessEntityId=${conversion.businessEntityId}`
    const response = await ApiServer.put(basePath, conversion)
    context.commit('updateConversion', response.data)
    return response.data
  },

  async fetchLandownerTypes (context) {
    const response = await ApiServer.get('landownerTypes')
    const sortedData = response.data.sort((a, b) => a.name.localeCompare(b.name))
    context.commit('setLandownerTypes', sortedData)
    return sortedData
  },

  async deleteLandownerType (context, landownerTypeId) {
    const response = await ApiServer.delete(`landownerTypes/${landownerTypeId}`)
    context.commit('removeLandownerType', landownerTypeId)
    return response.data
  },

  async createLandownerType (context, landownerType) {
    const response = await ApiServer.post('landownerTypes', landownerType)
    context.commit('addLandownerType', response.data)
    return response.data
  },

  async updateLandownerType (context, landownerType) {
    let basePath = `landownerTypes/${landownerType.landownerTypeId}`
    if (landownerType.businessEntityId) basePath += `?businessEntityId=${landownerType.businessEntityId}`
    const response = await ApiServer.put(basePath, landownerType)
    context.commit('updateLandownerType', response.data)
    return response.data
  },

  async fetchBidCostTypes (context) {
    const response = await ApiServer.get('bidCostTypes')
    const sortedData = response.data.sort((a, b) => a.name.localeCompare(b.name))
    context.commit('setBidCostTypes', sortedData)
    return sortedData
  },

  async deleteBidCostType (context, bidCostTypeId) {
    const response = await ApiServer.delete(`bidCostTypes/${bidCostTypeId}`)
    context.commit('removeBidCostType', bidCostTypeId)
    return response.data
  },

  async createBidCostType (context, bidCostType) {
    const response = await ApiServer.post('bidCostTypes', bidCostType)
    context.commit('addBidCostType', response.data)
    return response.data
  },

  async updateBidCostType (context, bidCostType) {
    const response = await ApiServer.put(`bidCostTypes/${bidCostType.bidCostTypeId}`, bidCostType)
    context.commit('updateBidCostType', bidCostType)
    return response.data
  },

  async fetchTimberCategories (context) {
    const response = await ApiServer.get('timberCategories')
    const sortedData = response.data.sort((a, b) => a.name.localeCompare(b.name))
    context.commit('setTimberCategories', sortedData)
    return sortedData
  },

  async deleteTimberCategory (context, timberCategoryId) {
    const response = await ApiServer.delete(`timberCategories/${timberCategoryId}`)
    context.commit('removeTimberCategory', timberCategoryId)
    return response.data
  },

  async createTimberCategory (context, timberCategory) {
    const response = await ApiServer.post('timberCategories', timberCategory)
    context.commit('addTimberCategory', response.data)
    return response.data
  },

  async updateTimberCategory (context, timberCategory) {
    let basePath = `timberCategories/${timberCategory.timberCategoryId}`
    if (timberCategory.businessEntityId) basePath += `?businessEntityId=${timberCategory.businessEntityId}`
    const response = await ApiServer.put(basePath, timberCategory)
    context.commit('updateTimberCategory', response.data)
    return response.data
  }
}

const searchAddressWithAzure = async (companyAddress) => {
  const query = `${companyAddress.addressLine1},${companyAddress.city},${companyAddress.state},${companyAddress.postalCode}`
  const format = 'JSON'
  const apiVersion = '1.0'
  const requestURL = `https://atlas.microsoft.com/search/address/${format}?api-version=${apiVersion}&subscription-key=${azureMapsApiKey()}&query=${query}`
  const encodedURL = encodeURI(requestURL)

  const { data } = await get(encodedURL)
  const results = data.results
  if (results.length > 0) {
    const position = results[0].position
    const address = results[0].address
    return {
      latitude: position.lat,
      longitude: position.lon,
      countrySubdivision: address.countrySubdivision,
      countrySecondarySubdivision: address.countrySecondarySubdivision
    }
  }

  throw new Error('No results with Azure.')
}

const searchCoordinatesWithAzure = async ({ latitude, longitude }) => {
  const query = `${latitude},${longitude}`
  const format = 'JSON'
  const apiVersion = '1.0'
  const requestURL = `https://atlas.microsoft.com/search/address/reverse/${format}?api-version=${apiVersion}&subscription-key=${azureMapsApiKey()}&query=${query}`
  const encodedURL = encodeURI(requestURL)

  const { data } = await get(encodedURL)
  const addresses = data.addresses
  if (addresses.length > 0) {
    const address = addresses[0].address
    return {
      countryCode: address.countryCode,
      countrySubdivision: address.countrySubdivision,
      countrySecondarySubdivision: address.countrySecondarySubdivision
    }
  }

  throw new Error('No results with Azure.')
}

const searchWeatherWithAzure = async ({ latitude, longitude, startDate, endDate, units }) => {
  const query = `${latitude},${longitude}`
  const format = 'JSON'
  const apiVersion = '1.0'
  const requestURL = `https://atlas.microsoft.com/weather/historical/actuals/daily/${format}?api-version=${apiVersion}&subscription-key=${azureMapsApiKey()}&unit=${units}&query=${query}&startDate=${startDate}&endDate=${endDate}`
  const encodedURL = encodeURI(requestURL)

  const { data } = await get(encodedURL)
  if (data) {
    return data
  }

  throw new Error('Error getting weather data from Azure.')
}

const searchCoordinatesWithFCC = async ({ latitude, longitude }) => {
  const { data } = await get(`https://geo.fcc.gov/api/census/area?lat=${latitude}&lon=${longitude}&format=json`)
  // FCC returns { input, results: [...] }. state_code is the 2-letter abbreviation
  // that matches our state-counties.json keys; county_name (e.g. "St. Clair County")
  // is reconciled against our list by canonicalCountyName in the caller.
  const results = data?.results ?? []
  if (results.length > 0) {
    const address = results[0]
    return {
      countrySubdivision: address.state_code,
      countrySecondarySubdivision: address.county_name
    }
  }

  throw new Error('No results with FCC.')
}
